/** Estilos compartidos de Reuniones (móvil primero; escritorio 2 columnas media-first). */

import { alpha } from "@mui/material/styles";
import { dashPageBgSx } from "../MyDashboard/myDashboardStyles";

/** Radio unificado de cards de reunión. */
export const MEETING_CARD_RADIUS = 3.5;

/** Gris neutro (no cian TON): contraste con cards blancas en los huecos. */
export const MEETING_SURFACE_BG = "#eef1f4";

/** Álbum galería: grupos en gris medio (contraste con thumbs blancos y el surface). */
export const meetingAlbumGroupSurfaceSx = (theme) => {
  const ink = theme.palette.text.primary;
  return {
    // Opaco: si no, en móvil el feed sticky/fijo deja ver el Summary detrás.
    bgcolor: theme.palette.mode === "dark" ? "#1a2330" : "#e6eaee",
    borderColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.14)
        : alpha(ink, 0.12),
  };
};

/** Ungrouped: gris un poco más marcado que los grupos (siempre opaco). */
export const meetingAlbumUngroupedSurfaceSx = (theme) => {
  const ink = theme.palette.text.primary;
  return {
    // Opaco obligatorio: el dock móvil es position:fixed sobre el feed.
    bgcolor: theme.palette.mode === "dark" ? "#222c3a" : "#dfe4e9",
    background: theme.palette.mode === "dark" ? "#222c3a" : "#dfe4e9",
    borderColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.2)
        : alpha(ink, 0.16),
  };
};

export const meetingPageShellSx = (contentMargin, theme) => {
  const isDark = theme?.palette?.mode === "dark";
  const background = isDark
    ? dashPageBgSx(theme).background
    : MEETING_SURFACE_BG;
  return {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    maxHeight: "100dvh",
    minHeight: "100dvh",
    boxSizing: "border-box",
    overflowX: "hidden",
    overflowY: { xs: "auto", md: "hidden" },
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    pt: { xs: 1, md: 1.25 },
    px: { xs: 1, sm: 1.5, md: 2, lg: 2.5 },
    pb: { xs: 11, md: 1.25 },
    ...contentMargin,
    alignItems: "stretch",
    background,
    bgcolor: isDark ? undefined : MEETING_SURFACE_BG,
  };
};

export const meetingSectionGap = 2;

/**
 * xs: 1 col · md+: 2 cols (app | media+acta)
 * railCollapsed: columna izquierda tipo icon-rail (modo trabajo)
 */
export const meetingDesktopSplitSx = (opts = {}) => {
  const railCollapsed = Boolean(opts.railCollapsed);
  const hideRail = Boolean(opts.hideRail);
  return {
    display: "grid",
    gap: { xs: 2, md: hideRail ? 0 : 2, lg: hideRail ? 0 : 2.25 },
    alignItems: { xs: "start", md: "stretch" },
    width: "100%",
    flex: { md: 1 },
    minHeight: { md: 0 },
    bgcolor: "transparent",
    background: "transparent",
    gridTemplateColumns: {
      xs: "1fr",
      md: hideRail
        ? "minmax(0, 1fr)"
        : railCollapsed
          ? "72px minmax(0, 1fr)"
          : "minmax(280px, 340px) minmax(0, 1fr)",
      lg: hideRail
        ? "minmax(0, 1fr)"
        : railCollapsed
          ? "76px minmax(0, 1fr)"
          : "minmax(280px, 360px) minmax(0, 1fr)",
      xl: hideRail
        ? "minmax(0, 1fr)"
        : railCollapsed
          ? "80px minmax(0, 1fr)"
          : "minmax(300px, 380px) minmax(0, 1fr)",
    },
    gridTemplateRows: { md: "minmax(0, 1fr)" },
  };
};

export const meetingDesktopAsideSx = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
  // En móvil el scroll es el body; minHeight:0 recortaba el acordeón de Datos
  minHeight: { xs: "auto", md: 0 },
  height: { md: "100%" },
  overflow: { xs: "visible", md: "auto" },
  // Hueco bajo el último card (Datos reunión) para no cortarlo al abrir
  pb: { xs: 3, md: 2 },
  boxSizing: "border-box",
  gridColumn: { xs: "1", md: "1" },
};

/** Rail estrecho: solo iconos de captura / herramientas. */
export const meetingDesktopAsideRailSx = {
  ...meetingDesktopAsideSx,
  gap: 1,
  pb: { xs: 1, md: 1 },
  // visible: si no, el Badge / chevron del rail se corta arriba
  overflow: "visible",
};

export const meetingCaptureRailSx = (theme) => ({
  ...meetingStackedPanelSx(theme),
  p: { xs: 0.55, md: 0.75 },
  pt: { xs: 0.65, md: 1 },
  display: "flex",
  // Móvil: wrap en 2 filas (si no, 8 iconos se pisan en iPhone).
  // Desktop: columna icon-rail.
  flexDirection: { xs: "row", md: "column" },
  flexWrap: { xs: "wrap", md: "nowrap" },
  alignItems: "center",
  justifyContent: { xs: "center", md: "flex-start" },
  columnGap: { xs: 0.35, md: 0.75 },
  rowGap: { xs: 0.35, md: 0.75 },
  position: { xs: "sticky", md: "relative" },
  top: { xs: 8, md: "auto" },
  zIndex: 26,
  overflow: "visible",
  maxWidth: "100%",
  boxSizing: "border-box",
});

export const meetingCaptureRailBtnSx = {
  width: { xs: 38, sm: 42, md: 48 },
  height: { xs: 38, sm: 42, md: 48 },
  borderRadius: 2,
  flex: "0 0 auto",
};

export const meetingDesktopMainSx = {
  display: "flex",
  flexDirection: "column",
  // Hueco entre bloques; el padre es blanco → no se ve franja de color
  gap: 2,
  minWidth: 0,
  minHeight: 0,
  height: { md: "100%" },
  overflowY: { md: "hidden" },
  overflowX: "hidden",
  gridColumn: { xs: "1", md: "2" },
  bgcolor: "transparent",
  background: "transparent",
};

/** Cuerpo scrolleable bajo la cabecera fija de sesión. */
export const meetingSessionBodySx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: { xs: "auto", md: "hidden" },
  pt: 0,
  pb: { xs: 2, md: 0 },
  WebkitOverflowScrolling: "touch",
  bgcolor: MEETING_SURFACE_BG,
  background: MEETING_SURFACE_BG,
};

/**
 * Bloque menú (separado). Sin dashHero: su gradiente+padding era la franja.
 */
export const meetingSessionHeaderSx = (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    position: "relative",
    flexShrink: 0,
    zIndex: 20,
    mb: 2,
    borderRadius: MEETING_CARD_RADIUS,
    border: "1px solid",
    borderColor: isDark ? alpha("#fff", 0.14) : alpha("#0b1a24", 0.12),
    bgcolor: isDark ? alpha("#152030", 0.98) : "#ffffff",
    background: isDark ? alpha("#152030", 0.98) : "#ffffff",
    boxShadow: isDark
      ? "none"
      : `0 1px 2px ${alpha("#0b1a24", 0.04)}, 0 4px 16px ${alpha("#0b1a24", 0.06)}`,
    overflow: "visible",
  };
};

/** Bloque Summary / galería: blanco sobre superficie gris. */
export const meetingStackedPanelSx = (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    p: 0,
    m: 0,
    borderRadius: MEETING_CARD_RADIUS,
    border: "1px solid",
    borderColor: isDark ? alpha("#fff", 0.14) : alpha("#0b1a24", 0.12),
    bgcolor: isDark ? alpha("#152030", 0.98) : "#ffffff",
    background: isDark ? alpha("#152030", 0.98) : "#ffffff",
    boxShadow: isDark
      ? "none"
      : `0 1px 2px ${alpha("#0b1a24", 0.04)}, 0 4px 16px ${alpha("#0b1a24", 0.06)}`,
  };
};

/** Cabecera de listado sticky (MeetingsPage). */
export const meetingStickyHeaderSx = (theme) => ({
  position: "sticky",
  top: 0,
  zIndex: 30,
  mb: meetingSectionGap,
  overflow: "visible",
  bgcolor:
    theme.palette.mode === "dark"
      ? "rgba(18,18,18,0.97)"
      : "rgba(255,255,255,0.97)",
  backdropFilter: "blur(10px)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 20px rgba(0,0,0,0.4)"
      : "0 8px 20px rgba(15,23,42,0.08)",
});

/** Feed media-first: pastillas 2 en 2. */
export const meetingMediaFeedSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
  },
  gap: { xs: 1.25, md: 1.35 },
  alignItems: "stretch",
};

/** Miniatura fija cuadrada (compacta) en el feed. */
export const meetingMediaThumbFrameSx = {
  position: "relative",
  width: "100%",
  aspectRatio: "1 / 1",
  flex: "0 0 auto",
  borderRadius: 2,
  overflow: "hidden",
  bgcolor: "grey.900",
};

/** Media rellenando la caja (cover). */
export const meetingMediaThumbMediaSx = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

/** @deprecated */
export const meetingMediaThumbSx = {
  ...meetingMediaThumbFrameSx,
};

export const meetingMediaFeedCardSx = (theme, highlighted) => ({
  display: "grid",
  gridTemplateColumns: {
    xs: "88px minmax(0, 1fr)",
    sm: "96px minmax(0, 1fr)",
    md: "104px minmax(0, 1fr)",
  },
  gap: { xs: 1, md: 1.15 },
  alignItems: "start",
  minWidth: 0,
  height: "100%",
  p: { xs: 1, md: 1.1 },
  borderRadius: 2,
  border: "1px solid",
  borderColor: highlighted ? "primary.main" : "divider",
  bgcolor:
    theme.palette.mode === "dark"
      ? "rgba(18,18,18,0.92)"
      : "rgba(255,255,255,0.98)",
  outline: highlighted ? "2px solid" : "none",
  outlineColor: highlighted ? "primary.main" : "transparent",
  outlineOffset: 1,
  transition: "border-color 0.2s, outline-color 0.2s",
});

/**
 * Pastilla de media en grupos — media-first (foto protagonista).
 * @param {object} flags
 * @param {boolean} [flags.selected] checkbox / multi-select
 * @param {boolean} [flags.highlighted] salto desde galería → fondo + franja suave
 */
export const meetingMediaGroupCardSx = (
  theme,
  highlightedOrFlags = false,
  maybeFlags,
) => {
  const flags =
    typeof highlightedOrFlags === "object" && highlightedOrFlags != null
      ? highlightedOrFlags
      : { selected: Boolean(highlightedOrFlags), ...maybeFlags };
  const selected = Boolean(flags.selected);
  const jump = Boolean(flags.highlighted);
  const compact = Boolean(flags.compact);
  const wash =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.success.main, 0.16)
      : alpha(theme.palette.success.main, 0.1);
  const accent =
    theme.palette.mode === "dark"
      ? theme.palette.success.light
      : theme.palette.success.main;

  return {
    position: "relative",
    display: "grid",
    gridTemplateColumns: compact
      ? "1fr"
      : {
          xs: "1fr",
          sm: "168px minmax(0, 1fr)",
        },
    gap: compact ? 0 : { xs: 1.1, sm: 1.35 },
    alignItems: "start",
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
    p: compact ? 0.5 : { xs: 1.1, sm: 1.2 },
    borderRadius: compact ? 2 : MEETING_CARD_RADIUS,
    border: "1px solid",
    borderColor: selected
      ? "primary.main"
      : theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(15,23,42,0.08)",
    bgcolor: jump
      ? wash
      : theme.palette.mode === "dark"
        ? "rgba(24,24,24,0.95)"
        : "#fff",
    boxShadow: selected
      ? `0 0 0 1px ${theme.palette.primary.main}`
      : jump
        ? `0 8px 24px ${alpha(accent, 0.18)}`
        : theme.palette.mode === "dark"
          ? "0 1px 0 rgba(255,255,255,0.04)"
          : "0 1px 2px rgba(15,23,42,0.04)",
    zIndex: jump ? 3 : selected ? 2 : 1,
    transition:
      "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.2s ease",
    "&::before": jump
      ? {
          content: '""',
          position: "absolute",
          left: 0,
          top: 10,
          bottom: 10,
          width: 4,
          borderRadius: "0 4px 4px 0",
          bgcolor: accent,
          pointerEvents: "none",
        }
      : undefined,
    "&:hover": {
      borderColor: selected ? "primary.main" : "primary.light",
      boxShadow: jump
        ? `0 10px 28px ${alpha(accent, 0.22)}`
        : theme.palette.mode === "dark"
          ? "0 8px 24px rgba(0,0,0,0.35)"
          : "0 8px 20px rgba(15,23,42,0.08)",
    },
  };
};

/** Miniatura de la pastilla media-first. */
export const meetingMediaThumbWrapSx = {
  position: "relative",
  cursor: "pointer",
  borderRadius: 2,
  overflow: "hidden",
  width: "100%",
  aspectRatio: { xs: "4 / 3", sm: "1 / 1" },
  bgcolor: "action.hover",
};

/** Lista vertical: hello-pangea no calcula bien el drop en CSS grid. */
export const meetingMediaGroupsFeedSx = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 1.25, md: 1.35 },
  alignItems: "stretch",
};

/** Rejilla densa modo galería (sin notas en tarjeta) — estilo Mac. */
export const meetingMediaGalleryGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(4, minmax(0, 1fr))",
    sm: "repeat(6, minmax(0, 1fr))",
    md: "repeat(8, minmax(0, 1fr))",
    lg: "repeat(10, minmax(0, 1fr))",
  },
  gap: { xs: 0.5, sm: 0.65 },
  alignItems: "stretch",
};

/** Bandeja de grupos abiertos (inline sticky o embebida en columna izquierda). */
export const meetingEmptyGroupsDockSx = (theme, { sticky = true } = {}) => ({
  position: sticky ? "sticky" : "relative",
  top: sticky ? { xs: 108, sm: 116 } : undefined,
  zIndex: sticky ? 25 : 2,
  mb: 1.5,
  p: 1,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "primary.light",
  bgcolor:
    theme.palette.mode === "dark"
      ? "rgba(18,18,18,0.96)"
      : "rgba(255,255,255,0.97)",
  backdropFilter: sticky ? "blur(10px)" : undefined,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 10px 24px rgba(0,0,0,0.45)"
      : "0 10px 24px rgba(15,23,42,0.12)",
});

/** @deprecated 3ª columna eliminada; se mantiene por si hay imports residuales. */
export const meetingDesktopSummarySx = {
  display: "none",
};

export const meetingActionBtnSx = {
  width: "100%",
  minHeight: { xs: 52, md: 48 },
  px: 2,
  fontWeight: 700,
  textTransform: "none",
  borderRadius: 2.5,
  fontSize: { xs: "1rem", md: "0.95rem" },
};

/**
 * Tile de captura (Record / Photo / Video / Gallery): mismo peso visual,
 * icono arriba + label, pensado para 4 en una fila en móvil.
 */
export const meetingCaptureActionTileSx = (accent) => ({
  flex: "1 1 0",
  minWidth: 0,
  maxWidth: "100%",
  minHeight: { xs: 72, sm: 76 },
  px: { xs: 0.35, sm: 0.5 },
  py: { xs: 0.85, sm: 1 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.35,
  borderRadius: 2.25,
  border: "1.5px solid",
  borderColor: alpha(accent, 0.42),
  bgcolor: alpha(accent, 0.12),
  color: accent,
  boxShadow: "none",
  textTransform: "none",
  fontWeight: 800,
  lineHeight: 1.1,
  WebkitTapHighlightColor: "transparent",
  "&:hover": {
    bgcolor: alpha(accent, 0.2),
    borderColor: alpha(accent, 0.65),
    boxShadow: `0 4px 12px ${alpha(accent, 0.18)}`,
  },
  "&:active": {
    transform: "scale(0.97)",
  },
  "&.Mui-disabled": {
    opacity: 0.42,
    borderColor: alpha(accent, 0.2),
    bgcolor: alpha(accent, 0.06),
    color: alpha(accent, 0.55),
  },
  "& .MuiButton-startIcon": {
    margin: 0,
  },
});

/** Barra de selección de media (galería): compacta, sin botones teal gordos. */
export const meetingSelectionBarSx = (theme) => {
  const ink = theme.palette.text.primary;
  const primary = theme.palette.primary.main;
  return {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    flexWrap: "wrap",
    rowGap: 0.5,
    mb: 1.15,
    flexShrink: 0,
    px: 1,
    py: 0.55,
    minHeight: 40,
    borderRadius: 2,
    border: "1px solid",
    borderColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.14)
        : alpha(ink, 0.1),
    bgcolor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(primary, 0.05),
    boxShadow:
      theme.palette.mode === "dark" ? "none" : `0 1px 0 ${alpha(ink, 0.04)}`,
  };
};

/** Toolbar de cabecera de sesión: misma altura y look. */
export const MEETING_HEADER_BTN_HEIGHT = 36;

export const meetingHeaderToolbarSx = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: { xs: 0.45, sm: 0.65, md: 0.75 },
  minWidth: 0,
  maxWidth: "100%",
};

/** Grupo de toolbar estilo Moodboard (label + icon buttons). */
export const meetingHeaderToolGroupSx = (theme, { accent = false } = {}) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: { xs: 0.1, sm: 0.15 },
  height: { xs: 32, sm: MEETING_HEADER_BTN_HEIGHT },
  minHeight: { xs: 32, sm: MEETING_HEADER_BTN_HEIGHT },
  maxHeight: { xs: 32, sm: MEETING_HEADER_BTN_HEIGHT },
  boxSizing: "border-box",
  px: { xs: 0.35, sm: 0.5 },
  py: 0,
  borderRadius: 2,
  // No overflow:hidden/auto aquí: corta el Badge por arriba
  overflow: "visible",
  bgcolor: accent
    ? alpha(theme.palette.primary.main, 0.04)
    : alpha(theme.palette.text.primary, 0.03),
  border: "1px solid",
  borderColor: accent ? alpha(theme.palette.primary.main, 0.18) : "divider",
  flex: "0 0 auto",
  flexShrink: 0,
  // Spans de Tooltip/disabled: no comprimir iconos
  "& > span": {
    flex: "0 0 auto",
    display: "inline-flex",
  },
});

/** Badge de cabecera dentro del icono (no sobresale del toolbar con overflow-x). */
export const meetingHeaderBadgeSx = {
  "& .MuiBadge-badge": {
    fontSize: "0.62rem",
    minWidth: 15,
    height: 15,
    padding: "0 3px",
    top: 3,
    right: 3,
  },
};

export const meetingHeaderToolGroupLabelSx = (accent = false) => ({
  px: 0.55,
  fontWeight: 800,
  fontSize: "0.62rem",
  letterSpacing: 0.04,
  color: accent ? "primary.main" : "text.secondary",
  textTransform: "uppercase",
  userSelect: "none",
  flexShrink: 0,
  lineHeight: 1.2,
});

export const meetingHeaderToolIconBtnSx = {
  width: { xs: 26, sm: 28 },
  height: { xs: 26, sm: 28 },
  flex: "0 0 auto",
  borderRadius: 1.25,
  color: "text.secondary",
  "&:hover": {
    color: "primary.main",
    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
  },
  "&.Mui-disabled": {
    opacity: 0.38,
  },
};

/** Free notes: mismo tamaño que el toolbar, un poco más de hueco entre iconos. */
export const meetingFreeNotesToolGroupSx = (
  theme,
  { accent = false } = {},
) => ({
  ...meetingHeaderToolGroupSx(theme, { accent }),
  gap: 0.85,
  px: 0.6,
});

export const meetingFreeNotesToolIconBtnSx = {
  ...meetingHeaderToolIconBtnSx,
};

export const meetingHeaderBtnSx = {
  height: MEETING_HEADER_BTN_HEIGHT,
  minHeight: MEETING_HEADER_BTN_HEIGHT,
  maxHeight: MEETING_HEADER_BTN_HEIGHT,
  px: 1.25,
  py: 0,
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.8125rem",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  boxShadow: "none",
  "& .MuiButton-startIcon": {
    marginRight: 0.6,
    marginLeft: -0.25,
    "& > *:nth-of-type(1)": { fontSize: "1.05rem" },
  },
  "&.Mui-disabled": {
    opacity: 0.48,
  },
};

export const meetingHeaderToggleSx = {
  height: MEETING_HEADER_BTN_HEIGHT,
  minHeight: MEETING_HEADER_BTN_HEIGHT,
  borderRadius: 2,
  overflow: "hidden",
  "& .MuiToggleButton-root": {
    height: MEETING_HEADER_BTN_HEIGHT,
    minHeight: MEETING_HEADER_BTN_HEIGHT,
    px: 1.25,
    py: 0,
    fontWeight: 800,
    fontSize: "0.75rem",
    lineHeight: 1,
    textTransform: "none",
    border: "1px solid",
    borderColor: "divider",
  },
};

export const meetingHeaderIconBtnSx = {
  width: { xs: 32, sm: MEETING_HEADER_BTN_HEIGHT },
  height: { xs: 32, sm: MEETING_HEADER_BTN_HEIGHT },
  flex: "0 0 auto",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
};

export const meetingChipRowSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 0.75,
  alignItems: "center",
  pt: 0.25,
  pb: 0.15,
};

/** Grid denso legacy (captura rápida sin notas). */
export const meetingGalleryGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    md: "repeat(auto-fill, minmax(120px, 1fr))",
    lg: "repeat(auto-fill, minmax(128px, 1fr))",
    xl: "repeat(auto-fill, minmax(140px, 1fr))",
  },
  gap: { xs: 1.25, md: 1.25 },
};

export const meetingCaptureCardSx = (theme) => ({
  ...meetingStackedPanelSx(theme),
  p: { xs: 1.75, sm: 2, md: 2 },
  overflow: "visible",
});

export const meetingAutocompleteSx = {
  width: "100%",
  minWidth: 0,
  "& .MuiAutocomplete-inputRoot": {
    flexWrap: "wrap",
    rowGap: 0.5,
  },
  "& .MuiAutocomplete-tag": {
    maxWidth: "calc(100% - 8px)",
    height: 28,
  },
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .MuiFormControl-root": {
    minWidth: 0,
  },
  "& .MuiOutlinedInput-root": {
    alignItems: "flex-start",
    py: 0.5,
  },
};

/** Listado: árbol + ficha en escritorio. `treeCollapsed` reduce la columna del árbol. */
export function meetingListSplitSx(
  treeCollapsed = false,
  captureParked = false,
) {
  return {
    display: "grid",
    gap: 2,
    alignItems: "stretch",
    width: "100%",
    flex: 1,
    minHeight: 0,
    gridTemplateColumns: {
      xs: "1fr",
      md: treeCollapsed
        ? captureParked
          ? "76px minmax(0, 1fr)"
          : "56px minmax(0, 1fr)"
        : "minmax(240px, 300px) minmax(0, 1fr)",
    },
    gridTemplateRows: { md: "minmax(0, 1fr)" },
  };
}

export const meetingListPaneSx = {
  minWidth: 0,
  minHeight: 0,
  height: { md: "100%" },
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  // El scroll va en el cuerpo del árbol; la cabecera BLABLATON queda fija.
  overflow: "hidden",
  boxSizing: "border-box",
};

/** Cuerpo scrollable bajo la cabecera fija del árbol. */
export const meetingListTreeBodySx = {
  flex: "1 1 auto",
  minHeight: 0,
  minWidth: 0,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
  // block: si es flex-column, los grupos hacen flex-shrink y se “aprietan” al expandir.
  display: "block",
  "& > * + *": {
    mt: 0.25,
  },
};

export const meetingDetailPaneSx = {
  minWidth: 0,
  minHeight: 0,
  height: { md: "100%" },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxSizing: "border-box",
  bgcolor: MEETING_SURFACE_BG,
  background: MEETING_SURFACE_BG,
};

/** @deprecated alias — usar meetingDetailPaneSx */
export const meetingPreviewPaneSx = meetingDetailPaneSx;
